import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { CommonUtils } from '../../../utils/common/common.utils';
import { ErrorMessageService } from '../error-message/errormessage.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorhandleService {
  constructor(
    private readonly errorMessageService: ErrorMessageService,
    private readonly commonUtils: CommonUtils,
  ) {}

  /**
   *Handles any error responses from BE and prints to console.
   *
   * @template T
   * @param {*} error
   * @param {string} [operation='operation']
   * @param {T} [result]
   * @return {*}
   * @memberof ErrorhandleService
   */
  public handleError<T>(
    errorResponse: HttpErrorResponse,
    code: number | null,
    operation = 'operation',
    bannerMessage: BANNER_TYPE = BANNER_TYPE.API_ERROR,
    result?: T,
    endpoint?: string,
  ) {
    if (errorResponse && code) {
      switch (code) {
        case 409:
        case 400:
          this.check400Status(errorResponse, bannerMessage, endpoint);
          break;
        case 401:
          this.commonUtils.updateBannerType(BANNER_TYPE.API_UNAUTHORIZED);
          break;
        case 403:
          this.commonUtils.updateBannerType(BANNER_TYPE.API_FORBIDDEN);
          break;
        case 404:
          this.commonUtils.updateBannerType(BANNER_TYPE.API_NOT_FOUND);
          break;
        case 500:
          if (bannerMessage == BANNER_TYPE.API_400_ERROR) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
          } else {
            this.commonUtils.updateBannerType(bannerMessage);
          }
          break;
        case 503:
          this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
          break;
        default:
          this.commonUtils.updateBannerType(bannerMessage);
          break;
      }

      // DO send the error to remote logging infrastructure
      console.error(errorResponse); // log to console instead

      // DO a better job of transforming error for user consumption
      this.log(`${operation} failed: ${errorResponse?.error?.message}`);
    }

    // Let the app keep running by returning an empty result.
    return of(result as T);
  }

  private log(message: string) {
    this.errorMessageService.add(`Service ${message}`);
  }

  private check400Status(
    errorResponse: HttpErrorResponse,
    bannerMessage: BANNER_TYPE = BANNER_TYPE.API_ERROR,
    endpoint?: string,
  ) {
    // ensuring the error handling for file upload, delivery and route-pickup does not happen here
    if (endpoint === 'fileupload' || endpoint === 'route-pickup') {
      this.commonUtils.updateBanner(false);
    }
    // ensuring the error handling for delivery does happen here if the error message is 'Invalid info notice number'
    // since this is a specific case where we do want to show the banner
    else if (
      endpoint === 'delivery' &&
      errorResponse.error?.message !== 'Invalid info notice number'
    ) {
      this.commonUtils.updateBanner(false);
    } else {
      this.set400Error(errorResponse, bannerMessage);
    }
  }

  private set400Error(
    errorResponse: HttpErrorResponse,
    bannerMessage: BANNER_TYPE,
  ) {
    if (
      bannerMessage == BANNER_TYPE.API_400_ERROR &&
      errorResponse?.error?.message != null
    ) {
      //If no specific error set try to set error message from BE
      this.commonUtils.updateBannerType400Error(errorResponse?.error?.message);
    } else {
      if (errorResponse?.error?.errors) {
        this.check400Specific400Error(errorResponse);
      } else {
        this.commonUtils.updateBannerType(BANNER_TYPE.API_ERROR);
      }
    }
  }

  private check400Specific400Error(errorResponse: HttpErrorResponse) {
    //TEMPORARY MEASURE.  REMOVE LATER
    if (
      errorResponse?.error?.errors[0]?.message === 'location.breakDurationError'
    ) {
      this.commonUtils.updateBannerType(
        BANNER_TYPE.LOCATION_BREAK_DURATION_ERROR,
      );
    } else {
      this.commonUtils.updateBannerType400Error(
        errorResponse?.error?.errors[0]?.message,
      );
    }
  }
}
